import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styles from './start-page.module.scss';

import { useDispatch } from 'react-redux';
import { scrollToTop } from '../../utils';
import { ecommerce, EcommerceAction } from '../../services/ecommerce.service';
import { SceneToken } from '../../enums/scene-token.enum';
import { StartPageContent } from '../../interfaces/start-page-content.interface';
import { HeaderContent } from '../../interfaces/header-content.interface';
import classNames from 'classnames';
import { VisualHeader } from './visual-header/visual-header';
import { Button } from '../common/button/button';
import { BonusList } from '../common/bonus-list/bonus-list';
import { IBonus } from '../../interfaces/bonus.interface';
import { appConfig } from '../../config/app.config';
import { Header } from './header/header';
import { Offer } from './offer/offer';
import { Description } from './description/descripiton';
import { FlyingBtn } from './flying-btn/flying-btn';
import { WhatsAppInfo } from '../../interfaces/whats-app-info.interface';
import { PopupWindowID } from '../../enums/popup-window-id.enum';
import { Animate } from '../animation/animation';
import { setActivePopupWidnowId, setQuizWasStarted } from '../../redux/quiz-slice';
import { displayFlyingButton, displayPopupWindow } from '../../redux/display-slice';
import { IQuizState } from '../../interfaces/quiz-state.interface';
import { ISettingsState } from '../../interfaces/setting-state.interface';
import { ICustomLabel } from '../../interfaces/custom-label.interface';
import { useContext } from 'react';
import { StylesContext } from '../../contexts/styles-context/styles.context';
import { useCustomStylesOnHover } from '../../hooks/use-custom-styles-on-hover.hook';
import { analyticsService } from '../../analytics.service';
import { AnalyticsEvent } from '../../enums/analytics-event.enum';
import { useAppSelector } from '../../hooks/redux.hook';
import { InstructionPopup } from '../popups/instruction-popup/instruction-popup';
import { useWhatsappForm } from '../../hooks/use-whatsapp-form.hook';
import { QuizHistoryService, browserHistoryService } from '../../services/history.service';
import { BrowserHistoryCommand } from '../../enums/browser-history-command.enum';
import { IQuestion } from '../../interfaces/question.interface';
import { useIntersection } from '../../hooks/use-intersection.hook';
import { isMobile } from 'react-device-detect';
import { StartPageFooter } from './start-page-footer/start-page-footer';
import { usePolicyAgree } from '../../hooks/use-policy-agree.hook';

interface StartPageProps {
  quizState: IQuizState;
  bonuses?: IBonus[]
  historyService: QuizHistoryService
  headerContent?: HeaderContent,
  hasFlyingButton?: boolean,
  hasWhatsAppButton?: boolean;
  wasOpened?: boolean
  hasSignupForm: boolean;
  initialPageIndex?: number
  PCcontent: StartPageContent;
  mobileContent?: StartPageContent;
  whatsApp?: WhatsAppInfo;
  footerContent?: string;
  footerDisclamer?: string | boolean;
  labelClickHandler: () => void;
  settings: ISettingsState
  domain: string,
  customLabel?: ICustomLabel
  firstQuestion: IQuestion
  visualLocation?: "left" | "right";
}

const ecommerceObject = {
  id: 'initial-view',
  name: 'Начальная страница'
};

export default function StartPage(props: StartPageProps) {
  const {
    headerContent,
    hasFlyingButton,
    hasWhatsAppButton,
    hasSignupForm,
    whatsApp,
    quizState,
    initialPageIndex,
    PCcontent,
    mobileContent,
    historyService,
    bonuses,
    footerContent,
    footerDisclamer,
    domain,
    settings,
    customLabel,
    firstQuestion,
    visualLocation
  } = props;
  const fromMobile = useMediaQuery({ maxWidth: 820 });
  const screenWidth768 = useMediaQuery({ minWidth: 768 });
  const screenWidth1024 = useMediaQuery({ minWidth: 1024 });
  const dispatch = useDispatch();
  const nextButton: React.MutableRefObject<HTMLButtonElement> = useRef(null);
  const currentSceneStatus = Boolean(historyService.getSceneStatus(SceneToken.START_PAGE));
  const flyingButtonActive = useAppSelector(state => state.display.flyingButton);
  const showWAButton = hasWhatsAppButton && fromMobile && whatsApp?.enabled;
  const hasHeader = Object.keys(headerContent || {})?.length > 0;
  const { styles: customStyles } = useContext(StylesContext);
  const { modifiedCustomStyles: {
    primaryButtons = {},
    minorButtons = {},
    flyingButton = {},
    offerMarkedText,
    allButtons = {}
  }, onMouseEnter, onMouseOut } = useCustomStylesOnHover(customStyles);

  const [content, setContent] = useState(PCcontent);

  const start = useCallback(() => {
    if (historyService.nextScene && historyService.nextScene.token == SceneToken.QUESTION) {
      browserHistoryService.perform(BrowserHistoryCommand.FORWARD);
    } else {
      let nextScene = {
        token: SceneToken.QUESTION,
        subState: { data: { index: 0, id: firstQuestion.id, distributor: firstQuestion.distributor } }
      }

      historyService.push(nextScene).then(() => scrollToTop());
      ecommerce.add(EcommerceAction.CART, ecommerceObject);
      browserHistoryService.perform(BrowserHistoryCommand.PUSH, nextScene);

      if (quizState.wasStarted) return;

      analyticsService.send(AnalyticsEvent.START);
      dispatch(setQuizWasStarted());
    }
  }, [historyService, quizState, dispatch, firstQuestion]);

  const startByScrollHandler = useCallback(() => {
    if (historyService.currentScene.token == SceneToken.SIGN_UP) return false;
    start();
    analyticsService.send(AnalyticsEvent.START_BY_SCROLL);
    return true;
  }, [start, historyService]);

  const { policyStatus, policyAgreementMode } = usePolicyAgree();
  const { intersectionObserverRef } = useIntersection(startByScrollHandler, Boolean(settings.startTestByScroll && (policyAgreementMode == "light" || policyStatus)));
  const { activePopupWindowID } = useAppSelector(state => state.quiz);
  const { popupWindow } = useAppSelector(state => state.display);

  const submitWhatsappForm = useWhatsappForm(whatsApp, ecommerceObject, 'start_page_whatsapp');

  useEffect(() => {
    if (isMobile && mobileContent) {
      setContent(mobileContent);
    }
  }, [mobileContent, PCcontent]);

  useEffect(() => {
    if (quizState.wasOpened && initialPageIndex === 0) {
      ecommerce.add(EcommerceAction.REVIEW, ecommerceObject);
    }
  }, [quizState.wasOpened, initialPageIndex]);

  const signUp = useCallback(() => {
    let nextScene = { token: SceneToken.SIGN_UP }

    historyService.push(nextScene, false).then(() => {
      scrollToTop();
      browserHistoryService.perform(BrowserHistoryCommand.PUSH, nextScene);
    });
  }, [historyService]);

  const buttonHandler = useCallback(() => start(), [start]);

  const flyingButtonHandler = useCallback(() => {
    start();
    analyticsService.send(AnalyticsEvent.START_BY_FLYING_BUTTON);
  }, [start]);

  const minorButtonHandler = useCallback((e: React.MouseEvent<HTMLElement>) => {
    ecommerce.add(EcommerceAction.CART, ecommerceObject);
    analyticsService.send(AnalyticsEvent.CLICK_MINOR_BUTTON);
    if (showWAButton) submitWhatsappForm(e);
    else if (content.minorButtonUrl) {
      window.open(content.minorButtonUrl, "_blank");
    } else signUp();

  }, [showWAButton, signUp, submitWhatsappForm, content]);

  const onClosePopup = () => {
    dispatch(displayPopupWindow(false));
    setTimeout(() => {
      dispatch(setActivePopupWidnowId(PopupWindowID.SIGN_UP));
      dispatch(displayPopupWindow(true));
      signUp();
    }, 300);
  }

  return (<>
    {activePopupWindowID == PopupWindowID.WHATSAPP_INSTRUCTIONS &&
      <InstructionPopup
        onButtonClick={() => submitWhatsappForm()}
        onClose={onClosePopup}
        active={popupWindow} />
    }
    <section className={classNames(styles.startPage, styles[`image-position-${visualLocation}`], { [styles.bigFixButtonBlock]: content.minorButtonText || hasWhatsAppButton, [styles.withoutHeader]: !hasHeader })}>
      {!(settings.noImageOnStartPageForMobile && !screenWidth1024) && <VisualHeader
        location={visualLocation}
        imagePosition={content.imagePosition}
        backgroundImage={appConfig().assets_url + content.image?.url}
        active={currentSceneStatus}
        caption={content.imageCaption}
      />}

      {hasFlyingButton && <FlyingBtn
        onClick={flyingButtonHandler}
        text={content.flyingButtonText}
        active={currentSceneStatus && flyingButtonActive}
        customStyles={flyingButton}
        onMouseOut={() => onMouseOut("flyingButton")}
        onMouseEnter={() => onMouseEnter("flyingButton")}
      />}

      <Animate duration={550} animation={visualLocation == "right" ? "start_page_animation_right" : "start_page_animation_left"} when={currentSceneStatus}>
        <section className={classNames(styles.container)}>

          {hasHeader && <Header
            historyService={historyService}
            active={currentSceneStatus}
            data={headerContent}
          />}

          <section className={styles.content}>
            <Offer text={content.offer} markColor={offerMarkedText?.color} dynamicText={content.dynamicText} />

            <Description text={content.description} />

            {screenWidth768 && <div
              onMouseEnter={() => dispatch(displayFlyingButton(false))}
              onMouseLeave={() => dispatch(displayFlyingButton(true))}
              className={classNames(styles.buttonContainer)}
            >
              <Button
                onMouseOut={() => onMouseOut("primaryButtons")}
                onMouseEnter={() => onMouseEnter("primaryButtons")}
                customStyles={{ ...allButtons, ...primaryButtons }}
                ref={nextButton}
                value={content.mainButtonText || "Рассчитать цену онлайн"}
                onClick={buttonHandler}
                mode='start'
              />
              {(showWAButton || content.minorButtonText !== false) && <Button
                onMouseOut={() => onMouseOut("minorButtons")}
                onMouseEnter={() => onMouseEnter("minorButtons")}
                customStyles={{ ...allButtons, ...minorButtons }}
                mode="minorButton"
                icon={showWAButton ? "whatsApp" : null}
                value={showWAButton ? (content.whatsAppButtonText || "Узнать по whatsapp") : (content.minorButtonText || "Записаться на прием")}
                onClick={minorButtonHandler}
              />}
            </div>}

            {(content.additionalButtons && screenWidth768) && <div className={styles.additionalButtons}>
              {content.additionalButtons?.map(button => {
                return <Button
                  mode={button.view}
                  icon={button.icon}
                  value={button.text}
                  onClick={() => {
                    if (button.link && button.task == "link") window.open(button.link, "_blank", "noopener noreferrer");
                    if (button.task == "sign-up") signUp();
                  }}
                />
              })}
            </div>}

            {/* {screenWidth768 && <Visits text={content.visitsText || "Сегодня тестов пройдено"} />} */}

            {bonuses.length > 0 && <>
              <BonusList
                bonuses={bonuses}
                active={false}
              />
            </>}

            {(!screenWidth768 && content.additionalButtons) && <div className={styles["additionalButtons"]}>
              {content.additionalButtons?.map(button => {
                return <Button
                  mode={button.view}
                  icon={button.icon}
                  value={button.text}
                  onClick={() => {
                    if (button.link && button.task == "link") window.open(button.link, "_blank", "noopener noreferrer");
                    if (button.task == "sign-up") signUp();
                  }}
                />
              })}
            </div>}
          </section>

          {<StartPageFooter
            label={true}
            text={footerContent}
            disclamer={footerDisclamer}
            domain={domain}
            customLabel={customLabel}
            whiteLabel={settings.whiteLabel}
            visitsText={content.visitsText || "Сегодня тестов пройдено"}
          />}

        </section>
      </Animate>

      {!screenWidth768 && <Animate duration={600} delay={150} animation={"go_up"} when={currentSceneStatus}>
        <div className={classNames(styles.buttonContainer)}>
          {((hasSignupForm || hasWhatsAppButton) && content.minorButtonText !== false) && <Button
            customStyles={{ ...allButtons, ...minorButtons }}
            mode="minorButton"
            icon={showWAButton ? "whatsApp" : null}
            value={showWAButton ? (content.whatsAppButtonText || "Узнать по whatsapp") : (content.minorButtonText || "Записаться на прием")} onClick={minorButtonHandler} />}
          <Button
            customStyles={{ ...allButtons, ...primaryButtons }}
            ref={nextButton} value={content.mainButtonText || "Рассчитать цену онлайн"}
            onClick={buttonHandler}
            mode='start'
          />
        </div>
      </Animate>}
    </section>
    <div className={styles.intersectionObserver} ref={intersectionObserverRef}></div>
  </>);
}